/**
 * React Query hooks for Clubs
 * 
 * Standard pattern for club data fetching and mutations.
 * Reference implementation for Issue #173.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { clubsApi, type CreateClubPayload, type UpdateClubPayload, type CreateClubResponse, type UpdateClubResponse, type MembershipRequest, type UpdateClubEventDefaultsPayload, type DiscoverClubsParams } from '../lib/api/clubs';
import { RequestDeduplicator, generateIdempotencyKey } from '../lib/deduplication';
import { apiPost } from '../lib/api';
import { useOroMutation, OroOperation } from './useOroMutation';
import { useAuth } from './useAuth';

/**
 * Fetch all clubs for current user
 *
 * Query Key: ['clubs']
 * Returns: Array of clubs with user's role in each
 */
export function useClubs() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['clubs'],
    queryFn: clubsApi.listMyClubs,
    staleTime: 1000 * 60 * 2, // 2 minutes - clubs list doesn't change frequently
    enabled: !!user, // Only fetch when authenticated
  });
}

/**
 * Discover public clubs with filters
 *
 * Query Key: ['clubs', 'discover', params]
 * Returns: Array of discoverable clubs
 * #635, #1355: Supports sport, meeting_mode, and region filters
 */
export function useDiscoverClubs(params?: DiscoverClubsParams) {
  return useQuery({
    queryKey: ['clubs', 'discover', params],
    queryFn: () => clubsApi.discoverClubs(params),
    staleTime: 1000 * 60 * 5, // 5 minutes - discovery results can be cached longer
  });
}

/**
 * Fetch single club by slug
 * 
 * Query Key: ['club', slug]
 * Returns: Club details with user's role
 */
export function useClub(slug: string) {
  return useQuery({
    queryKey: ['club', slug],
    queryFn: () => clubsApi.getClub(slug),
    enabled: !!slug,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

/**
 * Fetch single club by ID
 * 
 * Query Key: ['club', 'id', id]
 * Returns: Club details with user's role
 */
export function useClubById(id: string) {
  return useQuery({
    queryKey: ['club', 'id', id],
    queryFn: () => clubsApi.getClubById(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

/**
 * Create a new club
 * 
 * On success: Invalidates ['clubs'] to refresh the list
 */
export function useCreateClub() {
  const queryClient = useQueryClient();
  
  return useOroMutation<CreateClubResponse, CreateClubPayload>({
    mutationFn: async (payload: CreateClubPayload) => {
      // ═════ Layer 1: Frontend request deduplication ═════
      const deduplicator = RequestDeduplicator.getInstance();
      const idempotencyKey = generateIdempotencyKey(); // No userId – server-side scoped
      
      // Check if duplicate request already pending
      const pending = deduplicator.checkDuplicate(
        'POST /clubs',
        payload
      );
      
      if (pending) {
        console.log('[useCreateClub] Returning pending request for duplicate');
        return pending.promise as Promise<CreateClubResponse>;
      }
      
      // Create request with abort controller
      const controller = new AbortController();
      
      const promise = apiPost<CreateClubResponse>(
        '/clubs',
        payload,
        {
          idempotencyKey,
          signal: controller.signal,
        }
      ).catch((e: Error) => {
        if (e.name === 'AbortError') {
          console.log('[useCreateClub] Request aborted (duplicate detected)');
        }
        throw e;
      });
      
      // Register with deduplicator
      deduplicator.register(
        'POST /clubs',
        payload,
        promise,
        controller
      );
      
      return promise;
      // ═════ End Layer 1 ═════
    },
    onSuccess: () => {
      // Refresh clubs list after creating new club
      queryClient.invalidateQueries({ queryKey: ['clubs'] });
    },
    successToast: 'Club created',
    oro: {
      op: OroOperation.CreateClub,
      retryPolicy: 'never',
      requiresIdempotency: true,
      debugLabel: 'Club creation form',
    },
  });
}

/**
 * Update club details
 * 
 * On success: Invalidates both ['clubs'] list and specific ['club', slug] query
 */
export function useUpdateClub(slug: string) {
  const queryClient = useQueryClient();

  return useOroMutation<UpdateClubResponse, UpdateClubPayload>({
    mutationFn: (payload: UpdateClubPayload) => clubsApi.updateClub(slug, payload),
    onSuccess: () => {
      // Refresh both the list and the specific club
      queryClient.invalidateQueries({ queryKey: ['clubs'] });
      queryClient.invalidateQueries({ queryKey: ['club', slug] });
    },
    successToast: 'Club updated',
    oro: {
      op: OroOperation.UpdateClub,
      retryPolicy: 'never',
      debugLabel: 'Club settings form',
    },
  });
}

/**
 * Delete a club
 * 
 * On success: Invalidates ['clubs'] list
 */
export function useDeleteClub() {
  const queryClient = useQueryClient();
  
  return useOroMutation<void, string>({
    mutationFn: (slug: string) => clubsApi.deleteClub(slug),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clubs'] });
    },
    oro: {
      op: OroOperation.DeleteClub,
      retryPolicy: 'never',
      debugLabel: 'Club deletion confirmation',
    },
  });
}

/**
 * Join a club
 * 
 * On success: Invalidates both ['clubs'] list and specific ['club', slug] query
 */
export function useJoinClub() {
  const queryClient = useQueryClient();
  
  return useOroMutation<void, string>({
    mutationFn: (slug: string) => clubsApi.joinClub(slug),
    onSuccess: (_, slug) => {
      // Refresh both the list and the specific club (role will change)
      queryClient.invalidateQueries({ queryKey: ['clubs'] });
      queryClient.invalidateQueries({ queryKey: ['club', slug] });
    },
    oro: {
      op: OroOperation.JoinClub,
      retryPolicy: 'never',
      debugLabel: 'Club join button',
    },
  });
}

/**
 * Leave a club
 * 
 * On success: Invalidates both ['clubs'] list and specific ['club', slug] query
 */
export function useLeaveClub() {
  const queryClient = useQueryClient();
  
  return useOroMutation<void, string>({
    mutationFn: (slug: string) => clubsApi.leaveClub(slug),
    onSuccess: (_, slug) => {
      // Refresh both the list and the specific club (role will change)
      queryClient.invalidateQueries({ queryKey: ['clubs'] });
      queryClient.invalidateQueries({ queryKey: ['club', slug] });
    },
    oro: {
      op: OroOperation.LeaveClub,
      retryPolicy: 'never',
      debugLabel: 'Club leave confirmation',
    },
  });
}

/**
 * Transfer club ownership
 * 
 * On success: Invalidates both ['clubs'] list and specific club queries
 */
// Transfer ownership params (#714)
interface TransferOwnershipParams {
  newOwnerId: string;
  oldOwnerRole?: 'leader' | 'member' | 'leave';
}

export function useTransferOwnership(slug: string) {
  const queryClient = useQueryClient();

  return useOroMutation<void, TransferOwnershipParams>({
    mutationFn: ({ newOwnerId, oldOwnerRole = 'leader' }: TransferOwnershipParams) =>
      clubsApi.transferOwnership(slug, {
        new_owner_id: newOwnerId,
        old_owner_role: oldOwnerRole,
      }),
    onSuccess: () => {
      // Refresh both the list and the specific club (ownership changed)
      queryClient.invalidateQueries({ queryKey: ['clubs'] });
      queryClient.invalidateQueries({ queryKey: ['club', slug] });
      queryClient.invalidateQueries({ queryKey: ['clubMembers', slug] });
    },
    oro: {
      op: OroOperation.TransferClubOwnership,
      retryPolicy: 'never',
      debugLabel: 'Transfer ownership modal',
    },
  });
}

// ============================================================================
// MEMBERSHIP REQUESTS (#782)
// ============================================================================

/**
 * Request to join an invite-only club
 *
 * On success: Invalidates membership request query for the club
 */
export function useRequestMembership() {
  const queryClient = useQueryClient();

  return useOroMutation<{ request: MembershipRequest }, string>({
    mutationFn: (clubId: string) => clubsApi.requestMembership(clubId),
    onSuccess: (_, clubId) => {
      queryClient.invalidateQueries({ queryKey: ['membership-request', clubId] });
    },
    successToast: 'Membership request sent',
    oro: {
      op: OroOperation.RequestMembership,
      retryPolicy: 'never',
      debugLabel: 'Membership request button',
    },
  });
}

/**
 * Get current user's membership request for a club
 *
 * Query Key: ['membership-request', clubId]
 */
export function useMembershipRequest(clubId: string) {
  return useQuery({
    queryKey: ['membership-request', clubId],
    queryFn: () => clubsApi.getMyMembershipRequest(clubId),
    enabled: !!clubId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// ============================================================================
// CLUB EVENT DEFAULTS (#172 - v1.2)
// ============================================================================

/**
 * Fetch club's event defaults configuration
 *
 * Query Key: ['club-event-defaults', slug]
 * Returns: Event defaults or null if not configured
 * Requires auth - endpoint uses requireAuth middleware
 */
export function useClubEventDefaults(slug: string) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['club-event-defaults', slug],
    queryFn: () => clubsApi.getEventDefaults(slug),
    enabled: !!slug && !!user, // #1285: Wait for auth before fetching
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Update club's event defaults configuration
 *
 * On success: Invalidates ['club-event-defaults', slug]
 */
export function useUpdateClubEventDefaults(slug: string) {
  const queryClient = useQueryClient();

  return useOroMutation<{ ok: true }, UpdateClubEventDefaultsPayload>({
    mutationFn: (payload: UpdateClubEventDefaultsPayload) =>
      clubsApi.updateEventDefaults(slug, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['club-event-defaults', slug] });
    },
    successToast: 'Event defaults saved',
    oro: {
      op: OroOperation.UpdateClub,
      retryPolicy: 'never',
      debugLabel: 'Club event defaults form',
    },
  });
}
